import React from 'react';
import { motion } from 'framer-motion';
import { Check, X, Star } from 'lucide-react';

const fadeInUp = {
  initial: { opacity: 0, y: 60 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: "easeOut" }
};

const pricingPlansData = [
  { name: "Free", price: "$0", id: "free" },
  { name: "Developer", price: "$9", id: "developer", recommended: true },
  { name: "Business", price: "$29", id: "business" },
];

const featureComparisonData = [
  {
    category: "PDF Manipulation",
    features: [
      { name: "Merge PDFs", free: false, developer: true, business: true },
      { name: "Extract Pages", free: false, developer: true, business: true },
      { name: "Delete Pages", free: false, developer: true, business: true },
      { name: "Reorder Pages", free: false, developer: true, business: true },
      { name: "Rotate Pages", free: false, developer: true, business: true },
      { name: "Split PDF", free: false, developer: true, business: true },
    ],
  },
  {
    category: "Enhancement Tools",
    features: [
      { name: "Add Watermarks", free: false, developer: true, business: true },
      { name: "Add Page Numbers", free: false, developer: true, business: true },
      { name: "Flatten PDF", free: false, developer: true, business: true },
      { name: "Resize PDF", free: false, developer: true, business: true },
      { name: "Optimize PDF", free: true, developer: true, business: true },
      { name: "Crop PDF", free: false, developer: true, business: true },
    ],
  },
  {
    category: "File Conversion",
    features: [
      { name: "DOCX to PDF", free: false, developer: true, business: true },
      { name: "XLSX to PDF", free: false, developer: true, business: true },
      { name: "PPTX to PDF", free: false, developer: true, business: true },
      { name: "PDF to Word", free: false, developer: true, business: true },
      { name: "PDF to HTML", free: false, developer: true, business: true },
      { name: "PDF to Image", free: false, developer: true, business: true },
    ],
  },
  {
    category: "QR & Metadata",
    features: [
      { name: "Generate QR Code", free: true, developer: true, business: true },
      { name: "Smart QR Code", free: false, developer: true, business: true },
      { name: "Add Metadata", free: true, developer: true, business: true },
      { name: "Read Metadata", free: true, developer: true, business: true },
      { name: "Custom Properties", free: false, developer: true, business: true },
      { name: "Text Extraction", free: false, developer: true, business: true },
    ],
  },
  {
    category: "PDF Editor",
    features: [
      { name: "Edit PDF Content (Text/Images)", free: false, developer: false, business: true },
    ],
  },
  {
    category: "Templates",
    features: [
      { name: "Template Designer", free: false, developer: "Limited", business: "Full Access" },
      { name: "Template-Based PDF Generation", free: false, developer: true, business: true },
    ],
  },
  {
    category: "Support & Other",
    features: [
      { name: "API Access", free: true, developer: true, business: true },
      { name: "Rate Limits", free: "Basic", developer: "Standard", business: "High" },
      { name: "Email Support", free: false, developer: "72hr SLA", business: "24hr SLA" },
      { name: "Priority Support", free: false, developer: false, business: true },
      { name: "SLA Guarantee", free: false, developer: false, business: true },
    ],
  },
];


const FeatureMatrixSection = () => {
  return (
    <section id="features-table" className="py-20 bg-background border-t border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-16"
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={fadeInUp}
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4 font-heading text-primary-foreground">
            Compare Features <span className="gradient-text-brand">Across Our Plans</span>
          </h2>
          <p className="text-xl text-foreground max-w-3xl mx-auto">
            Find the perfect plan with the features you need to succeed. All prices in USD.
          </p>
        </motion.div>

        <motion.div
          className="overflow-x-auto bg-card rounded-xl shadow-2xl border border-border"
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={fadeInUp}
        >
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-background/50 sticky top-0 z-10">
              <tr>
                <th scope="col" className="px-4 sm:px-6 py-4 text-left text-sm font-semibold text-card-foreground font-heading sticky left-0 bg-background/50 z-20 min-w-[200px]">Feature</th>
                {pricingPlansData.map(plan => (
                  <th 
                    key={plan.id} 
                    scope="col" 
                    className={`px-4 sm:px-6 py-4 text-center text-sm font-semibold text-card-foreground font-heading min-w-[120px] ${plan.recommended ? 'bg-primary/10' : ''}`}
                  >
                    <div className="flex flex-col items-center">
                      {plan.recommended && (
                        <div className="flex items-center text-secondary mb-1">
                          <Star className="h-4 w-4 mr-1 fill-secondary" />
                          <span className="text-xs">Most Popular</span>
                        </div>
                      )}
                      <span>{plan.name}</span>
                      <span className="text-xs text-muted-foreground">{plan.price}</span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border bg-card">
              {featureComparisonData.map((categoryGroup) => (
                <React.Fragment key={categoryGroup.category}>
                  <tr>
                    <td colSpan={pricingPlansData.length + 1} className="px-4 sm:px-6 py-3 bg-muted/30 text-left text-sm font-semibold text-primary font-heading sticky left-0 bg-muted/30 z-10">
                      {categoryGroup.category}
                    </td>
                  </tr>
                  {categoryGroup.features.map((feature, idx) => (
                    <tr key={idx} className="hover:bg-background/30 transition-colors">
                      <td className="whitespace-nowrap px-4 sm:px-6 py-3 text-sm text-foreground sticky left-0 bg-card hover:bg-background/30 z-10">{feature.name}</td>
                      {pricingPlansData.map(plan => (
                        <td 
                          key={`${feature.name}-${plan.id}`} 
                          className={`whitespace-nowrap px-4 sm:px-6 py-3 text-sm text-center ${plan.recommended ? 'bg-primary/5' : ''}`}
                        >
                          {typeof feature[plan.id] === 'boolean' ? (
                            feature[plan.id] ? <Check className="h-5 w-5 text-green-500 mx-auto" /> : <X className="h-5 w-5 text-red-500/70 mx-auto" />
                          ) : (
                            <span className="text-xs text-foreground">{feature[plan.id]}</span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </motion.div>
      </div>
    </section>
  );
};

export default FeatureMatrixSection;